import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from '@/components/ui/badge';
import { MotionButton } from '@/components/ui/motion-button';
import { useBlogQueries } from "@/hooks/queries/useBlogQueries";
import { useAuth } from '@/hooks/useAuth';
import { staggerContainer, staggerItem, viewportOptions } from '@/lib/animations';
import { setPageMetadata } from '@/lib/utils';
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { motion } from 'framer-motion';
import { ChevronLeft, Clock, Edit, Tag, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

export const Route = createFileRoute('/_public/blog/$slug/')({
  component: BlogPostPage,
})

export default function BlogPostPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { slug } = useParams({ from: '/_public/blog/$slug/' });
  const { useFetchBlogPost, deleteBlogPostMutation } = useBlogQueries();
  const { data: post } = useFetchBlogPost(slug);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDelete = async () => {
    await deleteBlogPostMutation.mutateAsync(post.slug, {
      onSuccess: () => navigate({ to: '/blog' })
    });
  }

  const readingTime = post ? Math.ceil(post.content.split(' ').length / 200) : 0;

  useEffect(() => {
    // Create a description from the first 160 characters of content
    const description = post.content
      .replace(/[#*`]/g, '') // Remove markdown characters
      .slice(0, 160)
      .trim() + '...';

    setPageMetadata({
      title: post.title,
      description: post.description || description,
      ogImage: post.image || undefined
    });
  }, [post.title, post.content, post.description, post.image]);

  return (
    <>
      <div className="min-h-full bg-background p-12 relative z-10">
        <div className="mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions}
          >
            <motion.article 
              variants={staggerItem}
              className="max-w-3xl mx-auto"
            >
            <motion.div 
              variants={staggerItem}
              className="flex justify-between items-center mb-8"
            >
              <Link to="/blog">
                <MotionButton variant="ghost">
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to Blogs
                </MotionButton>
              </Link>

              {profile?.swimhomes_admin && (
                <div className="flex gap-2">
                  <MotionButton variant="outline" asChild>
                    <Link to="/blog/$slug/edit" params={{ slug: post.slug }}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Link>
                  </MotionButton>
                  <MotionButton
                    variant="destructive"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </MotionButton>
                </div>
              )}
            </motion.div>
            {post.image && (
              <motion.div 
                variants={staggerItem}
                className="relative aspect-video mb-8 rounded-lg overflow-hidden"
              >
                <img
                  src={post.image}
                  alt={post.title}
                  className="object-cover w-full h-full"
                />
              </motion.div>
            )}

            <motion.div 
              variants={staggerItem}
              className="mb-8"
            ></motion.div>
            
            <motion.h1 
              variants={staggerItem}
              className="text-4xl font-bold mb-4"
            >
              {post.title}
            </motion.h1>
            
            <motion.div 
              variants={staggerItem}
              className="flex items-center gap-4 text-muted-foreground mb-4"
            >
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{readingTime} Minute Read</span>
              </div>
              <span>By {post.author}</span>
            </motion.div>

            <motion.div 
              variants={staggerItem}
              className="flex gap-2 mb-4"
            >
              {post.tags.map((tag: string) => (
                <Badge key={tag}>
                  <Tag className="h-4 w-4 mr-1" />
                  {tag}
                </Badge>
              ))}
            </motion.div>

            <motion.div 
              variants={staggerItem}
              className="prose prose-lg dark:prose-invert max-w-none"
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {post.content}
              </ReactMarkdown>
            </motion.div>
            </motion.article>
          </motion.div>
        </div>

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the blog post.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );
}
